import { useCallback, useEffect, useState } from 'react'
import { get } from './lib/api'
import { me as whoAmI, signOut, type User } from './lib/auth'
import { api, go, resetContent, usePath, type Me } from './lib/classroom'
import { w } from './lib/words'
import { setSound, soundOn, celebrate } from './lib/fun'
import { deviceId } from './lib/push'
import { listenForNativeTaps, resyncNativePush } from './lib/native'
import Account from './components/Account'
import Admin from './components/Admin'
import EnvBanner from './components/EnvBanner'
import UpdateBar from './components/UpdateBar'
import InstallPrompt from './components/InstallPrompt'
import DebugPanel from './components/DebugPanel'
import ErrorBoundary from './components/ErrorBoundary'
import LanguageToggle from './components/LanguageToggle'
import Icon from './components/Icon'
import Welcome from './components/adulting/Welcome'
import StudentHome from './components/adulting/StudentHome'
import { ClassView, TeacherHome } from './components/adulting/Teacher'
import LiveStudent from './components/adulting/LiveStudent'
import LiveTeacher from './components/adulting/LiveTeacher'
import Present from './components/adulting/Present'
import Study from './components/adulting/Study'
import Pichi from './components/adulting/Pichi'
import Shop from './components/adulting/Shop'
import { BottomBar } from './components/adulting/Sheets'
import Legal, { LegalLinks } from './components/adulting/Legal'
import Guide from './components/adulting/Guide'
import Lessons, { LessonReview } from './components/adulting/Lessons'

export interface Health {
  app: string
  environment: 'local' | 'beta' | 'production'
  version: string
  commit: string
}

const JOIN_KEY = 'pendingJoin'

export default function App() {
  const path = usePath()
  const [user, setUser] = useState<User | null>(null)
  const [health, setHealth] = useState<Health | null>(null)
  const [ready, setReady] = useState(false)
  const [room, setRoom] = useState<Me | null>(null)
  const [sound, setSoundState] = useState(soundOn())
  // Runs this device has seen end, so the lock below lets go of them at once
  // rather than waiting for the next poll.
  const [endedRuns, setEndedRuns] = useState<string[]>([])

  const joinMatch = path.match(/^\/join\/([A-Za-z][0-9]{2}|[A-Za-z0-9]{6})$/)
  const joinCode = joinMatch?.[1]?.toUpperCase() ?? null

  const loadRoom = useCallback(async () => {
    try { setRoom(await api.me()) } catch { /* signed out */ }
  }, [])

  const refresh = useCallback(async () => {
    const u = await whoAmI()
    // What a person can see depends on who they are; never reuse the last one's.
    resetContent()
    setUser(u)
    if (u) await loadRoom()
  }, [loadRoom])

  useEffect(() => {
    void (async () => {
      const [u, h] = await Promise.all([whoAmI(), get<Health>('/api/health').catch(() => null)])
      setUser(u)
      setHealth(h)
      if (u) await loadRoom()
      setReady(true)
    })()
  }, [loadRoom])

  /*
   * The iOS app: a tapped "your class is live" opens that lesson, and an
   * already-granted APNs token is re-sent for whoever is signed in now (tokens
   * rotate; accounts switch). Both are no-ops in a browser and in an installed
   * shell without the push plugin.
   */
  useEffect(() => { listenForNativeTaps(go) }, [])
  useEffect(() => { if (user) void resyncNativePush(deviceId()) }, [user])

  // Arriving from the classroom QR: remember the code through sign-up, then join.
  useEffect(() => {
    if (joinCode) sessionStorage.setItem(JOIN_KEY, joinCode)
  }, [joinCode])
  useEffect(() => {
    const pending = sessionStorage.getItem(JOIN_KEY)
    if (!user || !pending) return
    sessionStorage.removeItem(JOIN_KEY)
    void api.join(pending).then(async () => { celebrate(); await loadRoom(); go('/') }).catch(() => go('/'))
  }, [user, loadRoom])

  // Fresh numbers on every screen change (practice counts, XP, the shop).
  useEffect(() => { if (user) void loadRoom() }, [path, user, loadRoom])

  /*
   * CLASS MODE. When a lesson goes live in one of a student's classes, the app
   * takes them to it and keeps them there: no menus, no bottom bar, and any
   * other route (a back swipe, a typed URL) snaps back to the lesson until it
   * ends. A web page cannot lock the phone itself — this locks the app.
   * Students are polled every few seconds so the pull happens fast.
   */
  // The room as the screens should see it: a lesson this device watched end is
  // not "live" any more, even before the next poll says so — otherwise its red
  // banner flashes on the home screen for a second.
  const shown = room ? { ...room, live: room.live.filter((r) => !endedRuns.includes(r.id)) } : null
  const liveRun = shown && !shown.teacher ? shown.live[0] : undefined
  useEffect(() => {
    if (!user || !room || room.teacher) return
    const id = setInterval(() => { void loadRoom() }, liveRun ? 20000 : 5000)
    return () => clearInterval(id)
  }, [user, room, liveRun, loadRoom])
  useEffect(() => {
    if (liveRun && path !== `/live/${liveRun.id}`) go(`/live/${liveRun.id}`)
  }, [liveRun, path])
  const locked = !!liveRun

  if (!ready) return <div className="boot">{w('connecting')}</div>

  // The projector is the whole screen — no bars, no footer.
  const present = path.match(/^\/present\/([^/]+)$/)
  if (present && user) return <ErrorBoundary><Present runId={present[1] as string} /></ErrorBoundary>

  const live = path.match(/^\/live\/([^/]+)$/)
  const study = path.match(/^\/study\/([^/]+)$/)
  const teach = path.match(/^\/teach\/([^/]+)$/)
  const lessonReview = path.match(/^\/lecciones\/(L[^/]+)$/)
  // Privacy, terms and support are public: the stores and a school's review
  // read them without an account. A live lesson still holds a student in class.
  const legal = !locked && (path === '/privacy' || path === '/terms' || path === '/support') ? path.slice(1) as 'privacy' | 'terms' | 'support' : null
  // The walkthrough is public too: a teacher puts the link on the projector on day one.
  const guide = locked ? null : path === '/guia' ? 'students' : path === '/guia/maestros' ? 'teachers' : null

  return (
    <ErrorBoundary>
      <UpdateBar />
      <EnvBanner health={health} />

      <header className="bar">
        <button className="brand" disabled={locked} onClick={() => go('/')} aria-label={w('home')} title={w('home')}><span className="brand-bird"><Pichi size={40} stage={2} look={room?.look} />{!locked && path !== '/' && <span className="home-hint" aria-hidden="true"><Icon name="home" size={12} /></span>}</span><span className="word">Adulting <b>101</b></span></button>
        <nav>
          <button
            aria-label={w('sound')}
            title={w('sound')}
            onClick={() => { setSound(!sound); setSoundState(!sound) }}
          >
            <Icon name={sound ? 'volume' : 'mute'} />
          </button>
          <LanguageToggle />
          {user && !locked && (
            <>
              <button onClick={() => go('/account')} aria-current={path === '/account'} aria-label={w('account')}><Icon name="person" /></button>
              {user.capabilities.includes('admin.access') && (
                <button onClick={() => go('/admin')} aria-current={path === '/admin'} aria-label={w('admin')}><Icon name="settings" /></button>
              )}
              <button aria-label={w('signOut')} title={w('signOut')} onClick={async () => { await signOut(); setRoom(null); await refresh(); go('/') }}><Icon name="door" /></button>
            </>
          )}
        </nav>
      </header>

      <main className={live ? 'wide' : ''}>
        {legal && <Legal page={legal} />}
        {guide && <Guide who={guide} />}
        {!legal && !guide && !user && <Welcome joinCode={joinCode} onSignedIn={refresh} />}
        {!legal && !guide && user && !room && <div className="boot">…</div>}
        {!legal && !guide && user && room && (
          <>
            {live && <LiveRoute runId={live[1] as string} user={user} room={room} onEnded={(id) => setEndedRuns((e) => [...e, id])} />}
            {path === '/tienda' && <Shop me={room} refresh={loadRoom} />}
            {path === '/lecciones' && room.teacher && <Lessons />}
            {lessonReview && room.teacher && <LessonReview id={lessonReview[1] as string} />}
            {study && <Study lessonId={study[1] as string} onXp={loadRoom} />}
            {teach && <ClassView id={teach[1] as string} />}
            {path === '/account' && <Account user={user} />}
            {path === '/admin' && <Admin />}
            {(path === '/' || joinCode) && (room.teacher
              ? <TeacherHome me={shown ?? room} refresh={loadRoom} />
              : <StudentHome me={shown ?? room} name={user.displayName} refresh={loadRoom} />)}
          </>
        )}
      </main>

      {user && room && (path === '/' || joinCode) && !locked && <BottomBar student={!room.teacher} onJoined={loadRoom} />}
      {user?.capabilities.includes('errors.read') && <DebugPanel environment={health?.environment ?? '—'} />}
      {!live && <InstallPrompt />}
      <footer className="muted small">
        {!locked && !legal && !guide && <LegalLinks />}
        {health ? `${health.version} · ${health.commit} · ${health.environment}` : ''}
      </footer>
    </ErrorBoundary>
  )
}

/** A live lesson: the teacher's console or the student's phone, decided by the server. */
function LiveRoute({ runId, user, room, onEnded }: { runId: string; user: User; room: Me; onEnded: (id: string) => void }) {
  const [role, setRole] = useState<'teacher' | 'student' | 'gone' | null>(null)
  useEffect(() => {
    void api.run(runId).then((r) => setRole(r.role)).catch(() => setRole('gone'))
  }, [runId])
  if (!role) return <div className="boot">{w('connecting')}</div>
  if (role === 'gone') return <p className="card">{w('ended')}</p>
  return role === 'teacher'
    ? <LiveTeacher runId={runId} room={room} onEnded={() => onEnded(runId)} />
    : <LiveStudent runId={runId} userId={user.id} xpBefore={room.xp} look={room.look} onEnded={() => onEnded(runId)} />
}
